function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/*
 * Openlayers doesn't give you a layer management control out of the box.
 * This should really be a class that extends ol.Control and uses openlayer's state rather than cloning.
 */
import OlAttribution from 'ol/control/Attribution';
import ReactDOM from 'react-dom';
import { Vector } from 'ol/layer';
import React, { Children, Fragment, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
var triggerIcon = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNS41NjgiIGhlaWdodD0iMTYuNDAxIj48cGF0aCBpZD0idHJpZ2dlckljb24tUGF0aF85NDY3IiBkYXRhLW5hbWU9IlBhdGggOTQ2NyIgZD0iTTEwLjc3NiAxNi4yMjVMNC40IDExLjMgMyAxMi4zODFsNy43ODQgNi4wMiA3Ljc4NC02LjAyLTEuNDEtMS4wOTItNi4zODMgNC45Mzd6bS4wMDktMi4xODVsNi4zNjUtNC45MjcgMS40MTgtMS4wOTNMMTAuNzg0IDIgMyA4LjAybDEuNDEgMS4wOTN6IiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtMyAtMikiIGZpbGw9IiMwYzIwM2QiLz48L3N2Zz4=";
import { useOpenLayers } from '../openlayers-context';
import usePrevious from '../hooks/usePrevious';
import { Arrow, PanelContainer, PanelList, PanelListItem, PanelListItemLabel, TriggerButton, TriggerText } from './LayerSwitcher.styles';
import Toggle from './Toggle';
import { usePopper } from 'react-popper';
import _ from 'lodash';

var useTogglePopper = boundaryElement => {
  var referenceElement = useRef(null);
  var popperElement = useRef(null);
  var [arrowElement, setArrowElement] = useState(null);
  var {
    styles,
    attributes
  } = usePopper(referenceElement.current, popperElement.current, {
    placement: 'top-end',
    modifiers: [{
      name: 'arrow',
      options: {
        element: arrowElement
      }
    }, {
      name: 'offset',
      options: {
        offset: [0, 10]
      }
    }, {
      name: 'preventOverflow',
      options: {
        padding: 20,
        boundary: boundaryElement
      }
    }]
  });
  return {
    styles,
    attributes,
    setArrowElement,
    referenceElement,
    popperElement
  };
};

var isRenderedAtCurrentZoom = (layer, view) => {
  var zoom = view.getZoom() || 0;
  return zoom > layer.getMinZoom() && zoom <= layer.getMaxZoom();
};

var ListItem = _ref => {
  var {
    id,
    isEnabled,
    isVisibleAtZoom,
    label,
    canToggle,
    onToggle
  } = _ref;
  var {
    map,
    featureCollection
  } = useOpenLayers();
  var prevIsEnabled = usePrevious(isEnabled);
  useEffect(() => {
    if (!map) return;
    var olLayer = map.getLayers().getArray().find(l => l.get('id') === id);

    if (prevIsEnabled !== isEnabled) {
      // toggle layer visibility
      olLayer === null || olLayer === void 0 ? void 0 : olLayer.setVisible(isEnabled);

      if (olLayer instanceof Vector) {
        var _featureCollection$cu;

        var features = olLayer.getSource().getFeatures();
        var featureList = featureCollection === null || featureCollection === void 0 ? void 0 : (_featureCollection$cu = featureCollection.current) === null || _featureCollection$cu === void 0 ? void 0 : _featureCollection$cu.getArray();
        features.forEach(feature => {
          var isDuplicateFeature = featureList === null || featureList === void 0 ? void 0 : featureList.includes(feature);

          if (isEnabled) {
            !isDuplicateFeature && (featureCollection === null || featureCollection === void 0 ? void 0 : featureCollection.current.push(feature));
          } else {
            isDuplicateFeature && (featureCollection === null || featureCollection === void 0 ? void 0 : featureCollection.current.remove(feature));
          }
        });
      }
    }
  }, [map, isEnabled, prevIsEnabled, id, featureCollection]);

  if (!map) {
    return /*#__PURE__*/React.createElement(Fragment, null);
  }

  var handleKeyPress = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      onToggle(id);
    }
  };

  var handleClick = () => {
    onToggle(id);
  };

  var disabled = !isVisibleAtZoom || !canToggle;
  return /*#__PURE__*/React.createElement(PanelListItem, {
    "aria-selected": isEnabled,
    role: "option",
    "aria-labelledby": "".concat(id, "-label"),
    onClick: handleClick,
    disabled: disabled
  }, /*#__PURE__*/React.createElement(Toggle, {
    id: id,
    readOnly: true,
    "aria-labelledby": "".concat(id, "-label"),
    checked: isEnabled && isVisibleAtZoom,
    disabled: disabled,
    onKeyPress: handleKeyPress,
    onClick: e => e.stopPropagation(),
    value: id
  }), /*#__PURE__*/React.createElement(PanelListItemLabel, {
    id: "".concat(id, "-label"),
    htmlFor: id
  }, label));
};

var LayerSwitcher = _ref2 => {
  var {
    hidden,
    viewportBoundaryRef,
    children,
    enableLayers,
    onEnableLayersChange
  } = _ref2;
  var {
    map,
    layerSwitcherTargetRef
  } = useOpenLayers();
  var [panelIsOpen, setPanelIsOpen] = useState(false);
  var listElement = useRef(null);
  var {
    styles,
    attributes,
    setArrowElement,
    referenceElement,
    popperElement
  } = useTogglePopper((viewportBoundaryRef === null || viewportBoundaryRef === void 0 ? void 0 : viewportBoundaryRef.current) || undefined);
  /**
   * ENABLED = OlLayer.visible (is enabled / will render if can)
   * VISIBLEATZOOM = Layer is rendered / Zoom is within OlLayers min / max zoom
   * Confusing I know.
   */

  var [layers, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case 'SET_LAYERS':
        return [...action.payload];

      case 'SET_LAYER_IS_VISIBLE':
        {
          return state.map(l => {
            var layerVisibility = action.payload.find(item => item.id === l.id);

            if (layerVisibility) {
              return _objectSpread(_objectSpread({}, l), {}, {
                isVisibleAtZoom: layerVisibility.value
              });
            } else {
              return l;
            }
          });
        }

      case 'TOGGLE_LAYER_IS_ENABLED':
        {
          return state.map(l => {
            return l.id === action.payload ? _objectSpread(_objectSpread({}, l), {}, {
              isEnabled: !l.isEnabled
            }) : l;
          });
        }

      case 'ENABLE_LAYERS':
        {
          // enable/disable layers using enableLayers prop
          return state.map(stateLayer => {
            var layerToEnable = action.payload.find(l => {
              if (l.id) {
                return l.id === stateLayer.id;
              } else {
                return l.reference === stateLayer.reference;
              }
            });

            if (layerToEnable) {
              return _objectSpread(_objectSpread({}, stateLayer), {}, {
                isEnabled: layerToEnable.display
              });
            } else {
              return stateLayer;
            }
          });
        }

      default:
        return state;
    }
  }, []);

  var setLayers = newLayers => {
    dispatch({
      type: 'SET_LAYERS',
      payload: newLayers
    });
  };

  var setLayerIsVisibleAtZoom = layersDetail => {
    dispatch({
      type: 'SET_LAYER_IS_VISIBLE',
      payload: layersDetail
    });
  };

  var toggleLayer = id => {
    dispatch({
      type: 'TOGGLE_LAYER_IS_ENABLED',
      payload: id
    });
  };

  var setEnableLayers = layersToEnable => {
    dispatch({
      type: 'ENABLE_LAYERS',
      payload: layersToEnable
    });
  };

  var layersRef = useRef();
  layersRef.current = layers;
  var enableLayersVisibilityRef = useRef();
  /**
   * Get enableLayers visibility on zoom change
   * Send these layers details to client through onEnableLayersChange
   */

  useEffect(() => {
    var enableLayersVisibility = [];

    var setEnableLayersVisibility = (layerRef, refType) => {
      if (layers) {
        var foundLayer = layers.find(layer => layer[refType] === layerRef);
        /**
         * Check for duplicate layer reference or id
         * So we can avoid sending duplicate values in onEnableLayersChange
         */

        var isDuplicate = !!enableLayersVisibility.find(l => l[refType] === (foundLayer === null || foundLayer === void 0 ? void 0 : foundLayer[refType]));

        if (foundLayer && !isDuplicate) {
          enableLayersVisibility.push({
            id: foundLayer.id,
            reference: foundLayer.reference,
            isVisibleAtZoom: !!foundLayer.isVisibleAtZoom
          });
        }
      }
    };

    enableLayers === null || enableLayers === void 0 ? void 0 : enableLayers.forEach(enableLayer => {
      if (enableLayer.id) {
        setEnableLayersVisibility(enableLayer.id, 'id');
      } else if (enableLayer.reference) {
        setEnableLayersVisibility(enableLayer.reference, 'reference');
      }
    }); // deep compare enableLayersVisibilityRef & enableLayersVisibility
    // so we don't call onEnableLayersChange unnecessarily

    if (!_.isEqual(enableLayersVisibilityRef.current, enableLayersVisibility) && enableLayersVisibility.length) {
      enableLayersVisibilityRef.current = enableLayersVisibility;
      onEnableLayersChange === null || onEnableLayersChange === void 0 ? void 0 : onEnableLayersChange(enableLayersVisibility);
    }
  }, [enableLayers, layers, onEnableLayersChange]);
  useEffect(() => {
    if (map) {
      var childLayers = Children.map(children, child => {
        // TODO - Can I check for child layers whilst still allowing composite components?
        if (!child) return;
        var {
          props: {
            id,
            label,
            reference
          }
        } = child;
        /* Check if children of <LayerSwitcher>
         * actually rendered to the map with an ID
         */

        var renderedOlLayersWithGivenId = map.getLayers().getArray().filter(l => l.get('id') === id);

        if (renderedOlLayersWithGivenId.length > 1) {
          console.error('Two layers were rendered with the same ID. Each layer must have a unique identifier and label.');
        }

        var layer = renderedOlLayersWithGivenId[0];

        if (!id || !label) {
          console.warn("Controlled layer ".concat(label || '', "is missing an id or label prop, so it isn't rendered in the layer switcher panel"));
          return;
        }

        if (!layer) {
          console.warn("OpenLayers did not render layer ".concat(label ? "'".concat(label, "' ") : '', "with given id").concat(id ? " '".concat(id, "'") : '.'));
          return;
        }

        return {
          id: layer === null || layer === void 0 ? void 0 : layer.get('id'),
          label: layer === null || layer === void 0 ? void 0 : layer.get('label'),
          reference,
          isEnabled: layer === null || layer === void 0 ? void 0 : layer.getVisible(),
          zIndex: layer === null || layer === void 0 ? void 0 : layer.getZIndex(),
          isVisibleAtZoom: isRenderedAtCurrentZoom(layer, map.getView()),
          canToggle: (layer === null || layer === void 0 ? void 0 : layer.get('canToggle')) !== false,
          minZoom: layer === null || layer === void 0 ? void 0 : layer.getMinZoom(),
          maxZoom: layer === null || layer === void 0 ? void 0 : layer.getMaxZoom()
        };
      });
      if (childLayers) setLayers(childLayers);
    }
  }, [map, children]);
  /**
   * Toggle layer display using enableLayers prop
   * To get newly added layers adding layers variable as dependency
   * Below logic to toggle layers gets overridden when LayerSwitcher UI is opened
   * This logic is used only when LayerSwitcher is hidden
   */

  useEffect(() => {
    var _map$getLayers$getArr;

    var mapLayers = (_map$getLayers$getArr = map === null || map === void 0 ? void 0 : map.getLayers().getArray()) !== null && _map$getLayers$getArr !== void 0 ? _map$getLayers$getArr : [];
    mapLayers.forEach(layer => {
      var layerId = layer === null || layer === void 0 ? void 0 : layer.get('id');
      var layerReference = layer.get('reference');
      var selectedLayer = enableLayers === null || enableLayers === void 0 ? void 0 : enableLayers.find(enLayer => {
        // id takes precedence over reference
        if (enLayer.id) {
          return enLayer.id === layerId;
        }

        if (enLayer.reference) {
          return enLayer.reference === layerReference;
        }

        return undefined;
      });

      if (selectedLayer) {
        layer.setVisible(selectedLayer.display);
      }
    });
  }, [enableLayers, layers, map]);
  /**
   * In above effect we toggle given enableLayers display state
   * In this effect we are reflecting updated layers display state into `layers` state
   */

  useEffect(() => {
    if (enableLayers !== null && enableLayers !== void 0 && enableLayers.length) {
      setEnableLayers(enableLayers);
    }
  }, [enableLayers]);
  /**
   * Set layer visibility on zoom level
   */

  var handleMoveEnd = useCallback(zoom => {
    if (layersRef.current && zoom) {
      var _layersRef$current;

      var layersVisibilityDetail = [];
      (_layersRef$current = layersRef.current) === null || _layersRef$current === void 0 ? void 0 : _layersRef$current.forEach(layer => {
        if (!layer.isVisibleAtZoom && !(zoom <= layer.minZoom || zoom > layer.maxZoom)) {
          layersVisibilityDetail.push({
            id: layer.id,
            value: true
          });
        } else if (layer.isVisibleAtZoom && (zoom <= layer.minZoom || zoom > layer.maxZoom)) {
          layersVisibilityDetail.push({
            id: layer.id,
            value: false
          });
        }
      }); // set changed layers visibility state at once outside of loop
      // to avoid firing multiple onEnableLayersChange event and renders

      setLayerIsVisibleAtZoom(layersVisibilityDetail);
    }
  }, []);
  var sortedLayers = useMemo(() => layers.sort((a, b) => b.zIndex - a.zIndex), [layers]);
  useEffect(() => {
    var handleClickOutside = e => {
      var _referenceElement$cur, _popperElement$curren;

      if (referenceElement !== null && referenceElement !== void 0 && (_referenceElement$cur = referenceElement.current) !== null && _referenceElement$cur !== void 0 && _referenceElement$cur.contains(e.target) || popperElement !== null && popperElement !== void 0 && (_popperElement$curren = popperElement.current) !== null && _popperElement$curren !== void 0 && _popperElement$curren.contains(e.target)) {
        return;
      }

      setPanelIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popperElement, referenceElement]);
  useEffect(() => {
    if (!map) return;

    var onMoveEnd = () => {
      var zoom = map.getView().getZoom();
      handleMoveEnd(zoom);
    };

    map.on('moveend', onMoveEnd);
    return () => {
      map.un('moveend', onMoveEnd);
    };
  }, [handleMoveEnd, map]);
  useEffect(() => {
    if (panelIsOpen) {
      var _listElement$current, _listElement$current$;

      // Focus first toggle in control panel
      (_listElement$current = listElement.current) === null || _listElement$current === void 0 ? void 0 : (_listElement$current$ = _listElement$current.querySelector('input')) === null || _listElement$current$ === void 0 ? void 0 : _listElement$current$.focus();
    }
  }, [panelIsOpen]); // If expanding layer switcher panel, collapse attribution panel first

  var onToggleLayersSelectPanel = () => {
    if (map && !panelIsOpen) {
      var controls = map.getControls().getArray();
      var attribution = controls.find(c => c instanceof OlAttribution);

      if (attribution) {
        var attributionIsCollapsed = attribution.getCollapsed();

        if (!attributionIsCollapsed) {
          attribution.setCollapsed(true);
        }
      }
    }

    setPanelIsOpen(!panelIsOpen);
  };

  if (!map) {
    return /*#__PURE__*/React.createElement(Fragment, null);
  }

  return /*#__PURE__*/React.createElement(Fragment, null, children, !hidden && panelIsOpen && /*#__PURE__*/React.createElement("div", _extends({
    ref: popperElement,
    style: styles.popper
  }, attributes.popper), /*#__PURE__*/React.createElement(PanelContainer, {
    "data-testid": "layers-panel"
  }, /*#__PURE__*/React.createElement(PanelList, {
    "aria-label": "layers-select-options",
    tabIndex: -1,
    ref: listElement
  }, sortedLayers === null || sortedLayers === void 0 ? void 0 : sortedLayers.map(layer => {
    return /*#__PURE__*/React.createElement(ListItem, _extends({
      key: layer.label,
      onToggle: toggleLayer
    }, layer));
  }))), /*#__PURE__*/React.createElement(Arrow, {
    ref: setArrowElement,
    style: styles.arrow
  })), !hidden && (layerSwitcherTargetRef !== null && layerSwitcherTargetRef !== void 0 && layerSwitcherTargetRef.current ? /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement(TriggerButton, {
    id: "layers-select-button",
    "aria-expanded": panelIsOpen,
    "aria-multiselectable": true,
    "aria-haspopup": "listbox",
    "aria-labelledby": "layers-select-button-text layers-select-button",
    ref: referenceElement,
    className: "layers-select-button",
    onClick: onToggleLayersSelectPanel
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center'
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: triggerIcon,
    alt: "Toggle layers select menu"
  }), /*#__PURE__*/React.createElement(TriggerText, {
    id: "layers-select-button-text"
  }, "Layers"))), layerSwitcherTargetRef.current) : console.error('Missing Controls container')));
};

export default LayerSwitcher;